import React, { useState, useEffect } from 'react';
import { isLoggedIn } from '../../../../Utility/helper';
import './Documents.css';

export default function Documents({ lead, onSave }) {
  const random_id = lead?.random_id;
  const loggedIn = isLoggedIn();

  const getLastThreeMonths = () => {
    const now = new Date();
    const months = [];
    for (let i = 1; i <= 3; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthName = date.toLocaleString('default', { month: 'short' });
      months.push(monthName);
    }
    return months;
  };

  const payslipMonths = getLastThreeMonths();

  const [documents, setDocuments] = useState([
    { id: 'aadhaar_front', label: 'Aadhaar Front', status: 'pending', file: null, meta: null, loading: false },
    { id: 'aadhaar_back', label: 'Aadhaar Back', status: 'pending', file: null, meta: null, loading: false },
    { id: 'pan', label: 'PAN Card', status: 'pending', file: null, meta: null, loading: false },
    { id: 'bank_statement', label: 'Bank Statement', status: 'pending', file: null, meta: null, loading: false },
    ...payslipMonths.map((month, index) => ({
      id: `payslip_${month.toLowerCase()}`,
      label: `Payslip - ${month}`,
      status: 'pending',
      file: null,
      meta: null,
      loading: false,
      blobKey: `payslip_month_${index + 1}_url`,
    })),
  ]);

  const [selectedDocId, setSelectedDocId] = useState(null);

  useEffect(() => {
    if (!lead) return;

    const urlMap = {
      aadhaar_front: lead.aadhaar_front_url,
      aadhaar_back: lead.aadhaar_back_url,
      pan: lead.pan_card_url,
      bank_statement: lead.bank_statement_url,
    };

    setDocuments((prev) =>
      prev.map((doc) => {
        const url = urlMap[doc.id] || (doc.blobKey ? lead[doc.blobKey] : null);
        if (!url) return doc;

        return {
          ...doc,
          status: 'uploaded',
          file: {
            previewUrl: url,
            type: inferMimeType(url),
          },
          meta: {
            timestamp: 'Preloaded',
            size: '—',
          },
        };
      })
    );
  }, [lead]);

  function inferMimeType(url) {
    if (!url) return 'image/jpeg';
    const ext = url.split('.').pop().toLowerCase();
    if (ext === 'pdf') return 'application/pdf';
    if (['jpg', 'jpeg', 'png'].includes(ext)) return 'image/jpeg';
    return 'application/octet-stream';
  }

  const handleDownload = async (url, label) => {
  try {
    const response = await fetch(url);
    const blob = await response.blob();

    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = `${label.replace(/\s+/g, '_')}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Optional: revoke the blob URL after download
    setTimeout(() => URL.revokeObjectURL(blobUrl), 100000);
  } catch (err) {
    console.error('❌ Download failed:', err);
    alert('Download failed. Please try again.');
  }
};

  const handleUpload = async (file) => {
    if (!file || !selectedDocId || !random_id) {
      console.warn('⚠️ Upload skipped due to missing field(s)');
      return;
    }

    setDocuments((prev) =>
      prev.map((doc) =>
        doc.id === selectedDocId ? { ...doc, loading: true } : doc
      )
    );

    const formData = new FormData();
    formData.append('file', file);
    formData.append('documentId', selectedDocId);

    try {
      const response = await fetch(
        `https://be.corebankers.in/telecalling_pl_doc-upload?random_id=${random_id}`,
        {
          method: 'POST',
          body: formData,
        }
      );

      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'Upload failed');

      setDocuments((prev) =>
        prev.map((doc) =>
          doc.id === selectedDocId
            ? {
                ...doc,
                status: 'uploaded',
                file: {
                  previewUrl: URL.createObjectURL(file),
                  type: file.type,
                },
                meta: {
                  timestamp: new Date().toLocaleString(),
                  size: `${(file.size / 1024).toFixed(2)} KB`,
                },
                loading: false,
              }
            : doc
        )
      );
    } catch (err) {
      console.error('❌ Upload error:', err);
      alert('Upload failed. Please try again.');
      setDocuments((prev) =>
        prev.map((doc) =>
          doc.id === selectedDocId ? { ...doc, loading: false } : doc
        )
      );
    }
  };

  const handleReplace = (id) => {
    setSelectedDocId(id);
    document.getElementById('fileInput').click();
  };

  const handleDelete = (id) => {
    setDocuments((prev) =>
      prev.map((doc) =>
        doc.id === id
          ? { ...doc, status: 'pending', file: null, meta: null }
          : doc
      )
    );
    if (selectedDocId === id) setSelectedDocId(null);
  };

  const anyUploaded = documents.some((doc) => doc.status === 'uploaded');
  const selectedDoc = documents.find((doc) => doc.id === selectedDocId);

  return (
    <div className="documents-container">
      <div className="documents-left-panel">
        <h3>📁 Documents</h3>
        {documents.map((doc) => (
          <div
            key={doc.id}
            className={`doc-item ${doc.id === selectedDocId ? 'selected' : ''}`}
            onClick={() => setSelectedDocId(doc.id)}
          >
            {doc.label} [{doc.status === 'uploaded' ? '✅' : '📤'}]
          </div>
        ))}
      </div>

      <div className="documents-right-panel">
        {selectedDoc ? (
          <>
            <div className="doc-header">
              <h3>🔍 {selectedDoc.label}</h3>
              {selectedDoc.file?.previewUrl && (
                <button
                  className="download-button"
                  onClick={() => handleDownload(selectedDoc.file.previewUrl, selectedDoc.label)}
                >
                  ⬇️ Download
                </button>
              )}
            </div>

            {selectedDoc.loading ? (
              <p>⏳ Uploading...</p>
            ) : selectedDoc.file ? (
              selectedDoc.file.type === 'application/pdf' ? (
                <iframe
                  src={selectedDoc.file.previewUrl}
                  title="PDF Preview"
                  className="preview-pdf"
                />
              ) : (
                <img
                  src={selectedDoc.file.previewUrl}
                  alt="Preview"
                  className="preview-image"
                />
              )
            ) : (
              <p>No file uploaded</p>
            )}

            <ul>
              <li>📦 Size: {selectedDoc.meta?.size || '—'}</li>
            </ul>

            {loggedIn && !selectedDoc.loading && (
              selectedDoc.file ? (
                <>
                  <button onClick={() => handleReplace(selectedDoc.id)}>Replace</button>
                  <button onClick={() => handleDelete(selectedDoc.id)}>Delete</button>
                </>
              ) : (
                <button onClick={() => handleReplace(selectedDoc.id)}>Upload</button>
              )
            )}
          </>
        ) : (
          <p>Select a document to view</p>
        )}

        <input
          id="fileInput"
          type="file"
          style={{ display: 'none' }}
          onChange={(e) => handleUpload(e.target.files[0])}
        />

        <div style={{ marginTop: '2rem' }}>
          {loggedIn && (
            <button disabled={!anyUploaded} onClick={() => alert('Submitted!')}>
              🔒 Submit Documents
            </button>
          )}
        </div>
      </div>
    </div>
  );
}